import React, { ReactElement } from 'react';

import { Avatar, Chip, capitalize } from '@mui/material';

import { ProjectStatusVariant } from 'state/userAdminSlices';

import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import {
  background_gray_light_2,
  primary_text_color,
  stl_secondary_hilited_background_color,
} from 'theme/cemTheme';

interface ProjectStatusChipProps {
  icon?: ReactElement;
  label: string;
  color: {
    bg: string;
    icon: string;
    text?: string;
  };
}

export const projectStatusChipVariants: Record<
  ProjectStatusVariant,
  ProjectStatusChipProps
> = {
  draft: {
    label: 'Draft',
    icon: <FontAwesomeIcon name="pen-to-square" />,
    color: {
      bg: 'rgba(68, 68, 68, 0.40)',
      icon: stl_secondary_hilited_background_color,
    },
  },
  pending_review: {
    label: 'In Review',
    icon: <FontAwesomeIcon name="user-magnifying-glass" />,
    color: {
      bg: 'rgba(244, 199, 95, 0.20)',
      icon: '#F4C75F',
    },
  },
  fetching: {
    label: 'Fetching',
    icon: <FontAwesomeIcon name="arrows-turn-to-dots" />,
    color: {
      bg: 'rgba(78, 141, 194, 0.20)',
      icon: '#4E8DC2',
    },
  },
  active: {
    label: 'Active',
    icon: <FontAwesomeIcon name="octagon-check" />,
    color: {
      bg: 'rgba(69, 139, 75, 0.20)',
      icon: '#458B4B',
    },
  },
  inactive: {
    label: 'Inactive',
    icon: <FontAwesomeIcon name="hexagon-minus" />,
    color: {
      bg: 'rgba(137, 81, 41, 0.20)',
      icon: '#895129',
    },
  },
};

export function ProjectStatusBadge({
  status,
  micro = false,
}: {
  status: string;
  micro?: boolean;
}) {
  const variant = projectStatusChipVariants[
    status.toLowerCase() as ProjectStatusVariant
  ] || {
    icon: <FontAwesomeIcon name="circle-question" />,
    label: capitalize(status.toLowerCase().replace('_', ' ')),
    color: { icon: primary_text_color, bg: background_gray_light_2 },
  };
  const { icon, label, color } = variant;

  if (micro) {
    return (
      <Avatar
        className={`project_status_badge_${status.toLowerCase()}`}
        sx={{
          color: primary_text_color,
          backgroundColor: color.bg,
          '& svg': {
            color: color.icon,
          },
        }}
      >
        {icon}
      </Avatar>
    );
  } else {
    return (
      <Chip
        icon={icon}
        label={label}
        className={`project_status_badge_${status.toLowerCase()}`}
        sx={{
          gap: '2px',
          padding: '6px',
          color: primary_text_color,
          backgroundColor: color.bg,
          '& .MuiChip-label': {},
          '& .MuiChip-icon': {
            color: color.icon,
          },
        }}
        size="small"
      />
    );
  }
}
